<template>
  <div v-if="show" class="language-dialog" :class="hasResumeView ? 'has-resume-view' : ''">
    <div class="language-container">
      <div class="language-list-editor">
        <template v-for="(language, index) in languageList">
          <div class="language-table" :class="!language.showEdit && 'show-table'" :key="index">
            <div class="head">
              <div class="title" @click="editorLanguage(language, index)">{{ `${$t(`${i18nPrefix}.title.language`)} ${index + 1}` }}</div>
              <div class="fold-btn" :class="language.showEdit ? 'rotated-icon' : ''" @click="editorLanguage(language, index)">
                <i class="iconfont icondown"></i>
              </div>
              <el-popconfirm
                :confirm-button-text="$t('common.btn.confirm')"
                :cancel-button-text="$t('common.btn.cancel')"
                icon="el-icon-info"
                icon-color="red"
                :title="$t('common.message.confirmDelete')"
                @confirm="deleteLanguage(language, index)"
              >
                <template #reference>
                  <div class="delete-btn">
                    <i class="el-icon-close"></i>
                  </div>
                </template>
              </el-popconfirm>
            </div>
            <div class="content">
              <el-table
                v-if="!language.showEdit"
                key="_vid"
                :ref="`languageTable_${index}`"
                :show-header="false"
                :data="[language]"
                :row-class-name="getRowClass"
              >
                <el-table-column class-name="language-name" prop="languageName"></el-table-column>
                <el-table-column prop="levelName"></el-table-column>
                <el-table-column prop="certificationName"></el-table-column>
                <el-table-column prop="score"></el-table-column>
              </el-table>
              <LanguageEdit
                v-else
                class="language-editor editor"
                :ref="`languageEdit_${index}`"
                :setting="setting"
                :language="language"
                :languageInfoList="languageInfoList"
                :levelList="levelList"
                :certificationList="certificationList"
              >
              </LanguageEdit>
            </div>
          </div>
        </template>
        <div class="add-btn-group">
          <el-button
            type="text"
            class="add-btn"
            icon="iconfont iconjia"
            @click="handleAddBtnClick"
          >
            {{ $t(`${i18nPrefix}.btn.addLanguage`) }}
          </el-button>
        </div>
        <div v-if="pageSettings.showLanguageTips" class="error-tips language-tips">
          {{ $t(pageSettings.languageTipsText) }}
        </div>
      </div>
    </div>
    <RectLoading v-show="loading" class="loading"></RectLoading>
  </div>
</template>

<script>
import _ from 'lodash'
import mixin from './mixin'
import LanguageEdit from './language.vue'
import { mapActions } from 'vuex'
import { addI18nProperty } from '@/i18n'
import RectLoading from '@/components/loading/rectLoading.vue'

let vid = 0

export default {
  mixins: [mixin],
  components: {
    LanguageEdit,
    RectLoading
  },
  props: {
    setting: {
      type: Object,
      required: false
    },
    hasResumeView: {
      type: Boolean,
      default: false
    },
    showEdit: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      hasForm: false,
      i18nPrefix: 'admin.application.candidate',
      stepData: {},
      languageInfoList: [],
      levelList: [],
      languageList: [],
      pageSettings: {
        showLanguageTips: false,
        languageTipsText: ''
      },
      certificationCheckField: ['certification', 'score']
    }
  },
  computed: {
    requiredField () {
      return this.setting?.requiredField || []
    }
  },
  methods: {
    ...mapActions('application', ['getLanguageList', 'getLanguageLevelList', 'getLanguageCertificationList']),

    formatSimpleJobInfo (infoItem) {
      const info = {
        ...infoItem,
        value: infoItem.id || infoItem.value
      }
      addI18nProperty(info, 'i18nName', 'cn_name', 'en_name')
      return info
    },
    async loadData () {
      if (!this.dataLoader) {
        this.dataLoader = Promise.all([
          this.loadLanguageList(),
          this.loadLanguageLevelList(),
          this.loadCertificationList()
        ])
      }
      await this.dataLoader
    },
    async loadLanguageList () {
      const resp = await this.getLanguageList()
      if (resp) {
        this.languageInfoList = _.map(resp.languages, this.formatSimpleJobInfo)
      }
      return this.languageInfoList
    },
    async loadLanguageLevelList () {
      const resp = await this.getLanguageLevelList()
      if (resp) {
        this.levelList = _.map(resp.language_levels, this.formatSimpleJobInfo)
      }
      return this.levelList
    },
    async loadCertificationList () {
      const resp = await this.getLanguageCertificationList()
      if (resp) {
        this.certificationList = _.map(resp.certifications, this.formatSimpleJobInfo)
      }
      return this.certificationList
    },
    async editorLanguage (language, index) {
      await this.loadData()
      const showEdit = language.showEdit
      if (showEdit) {
        const data = await this.$refs[`languageEdit_${index}`][0]?.submit()
        if (!data) {
          return
        }
        language.valid = true
        const languageInfo = _.find(this.languageInfoList, { id: data.language })
        const level = _.find(this.levelList, { id: data.level })
        const certification = _.find(this.certificationList, { id: data.certification })
        language.languageName = languageInfo?.i18nName || ''
        language.levelName = level?.i18nName || ''
        language.certificationName = certification?.i18nName || ''
        _.merge(language, data)
      }
      language.showEdit = !showEdit
    },
    deleteLanguage (language, index) {
      this.languageList.splice(index, 1)
    },
    async handleAddBtnClick () {
      await this.loadData()
      const language = {
        _vid: ++vid,
        showEdit: true
      }
      this.languageList.push(language)
    },
    async setStepDataToCandidateInfo () {
      const candidateInfo = this.candidateInfo
      this.$set(candidateInfo, 'languageInfo', _.map(this.languageList, item => {
        const languageInfo = _.find(this.languageInfoList, { id: item.language })
        const level = _.find(this.levelList, { id: item.level })
        const certification = _.find(this.certificationList, { id: item.certification })
        item.languageName = languageInfo?.i18nName || ''
        item.levelName = level?.i18nName || ''
        item.certificationName = certification?.i18nName || ''
        const info = { ...item }
        _.omit(info, ['_vid', 'valid'])
        return info
      }))
    },
    async getStepData () {
      await this.loadData()
      const languageInfo = this.candidateInfo.languageInfo
      this.languageList = _.map(languageInfo, (item, index) => {
        const languageInfo = _.find(this.languageInfoList, { id: item.language })
        const level = _.find(this.levelList, { id: item.level })
        const certification = _.find(this.certificationList, { id: item.certification })
        return {
          ...item,
          languageName: languageInfo?.i18nName || '',
          levelName: level?.i18nName || '',
          certificationName: certification?.i18nName || '',
          _vid: index + 1,
          showEdit: _.isBoolean(item.showEdit) ? item.showEdit : this.showEdit
        }
      })
      vid = this.languageList.length
      return this.languageList
    },
    async customValidate () {
      const length = this.languageList.length
      let isValid = true
      await Promise.all(_.times(length, async index => {
        const languageRef = this.$refs[`languageEdit_${index}`]
        if (languageRef && languageRef.length > 0) {
          const data = await languageRef[0].submit()
          if (data) {
            _.merge(this.languageList[index], data)
          } else {
            isValid = false
          }
        }
      }))
      return isValid
    },
    async validate () {
      let isValid = true
      if (this.hasForm) {
        isValid = await this.$refs.form.submit()
      }
      if (isValid) {
        isValid = await this.customValidate()
      }
      const languageValid = this.checkLanguageList(this.languageList)
      if (!languageValid) {
        this.pageSettings.showLanguageTips = true
        this.pageSettings.languageTipsText = `${this.i18nPrefix}.message.fieldRequired`
        isValid = false
      } else {
        this.pageSettings.showLanguageTips = false
        this.pageSettings.languageTipsText = ''
      }
      return Boolean(isValid)
    },
    checkLanguageList (languageList) {
      let isValid = true
      if (!languageList || languageList.length === 0) {
        return isValid
      }
      _.forEach(languageList, item => {
        let valid = true
        let skipCertificationValid = false
        if (item.level === 'NAT' || item.language !== 'ENG') {
          skipCertificationValid = true
        }
        _.forEach(this.requiredField, fieldName => {
          if ((!skipCertificationValid || !this.certificationCheckField.includes(fieldName)) &&
              (_.isNil(item[fieldName]) || item[fieldName] === '')) {
            valid = false
            isValid = false
          }
        })
        this.$set(item, 'valid', valid)
      })
      return isValid
    },
    getRowClass ({ row }) {
      if (row && row.valid === false) {
        return 'red'
      }
      return ''
    }
  },
  watch: {
    languageList: {
      handler () {
        this.checkLanguageList(this.languageList)
      },
      immediate: true
    }
  }
}
</script>

<style lang="scss" scoped>
.language-dialog {
  position: relative;

  .language-container {
    padding: 0 20px;
    position: relative;

    &:last-child {
      margin-bottom: 0;
    }

    .container-title {
      background: #f7f7f7;
      font-size: 16px;
      font-weight: bold;
      transform: translateY(-50%);
      position: absolute;
      top: 0px;
      left: 10px;
    }

    .container-tip {
      margin-bottom: 10px;
    }
    .language-list-editor {
      .language-list {
        margin-top: 20px;
        margin-bottom: 40px;

        .list-item {
          padding: 5px 10px;
          border: 1px solid #e7e7e7;
          background: #f1f1f1;

          &:not(:first-child) {
            margin-top: 10px;
          }

          &.ghost {
            opacity: 0.5;
            background-color: #cecece;
          }

          .item-title {
            display: flex;
            line-height: 16px;

            .item-icon {
              line-height: 20px;

              .icon {
                display: inline-block;
                width: 14px;
                height: 14px;
                background-repeat: no-repeat;
                background-size: contain;
              }

              .text-icon {
                background-image: url(~@/assets/image/openQuestion/text.png);
              }

              .select-icon {
                background-image: url(~@/assets/image/openQuestion/select.png);
              }

              .checkbox-icon {
                background-image: url(~@/assets/image/openQuestion/checkbox.png);
              }

              .attachment-icon {
                background-image: url(~@/assets/image/openQuestion/attachment.png);
              }

              .video-icon {
                background-image: url(~@/assets/image/openQuestion/video.png);
              }
            }

            .item-name {
              flex: 1;
              margin-left: 10px;
              color: #3b3b3b;
            }

            .item-btn-group {
              display: flex;
              color: #0096cc;
              align-items: center;

              .item-btn {
                cursor: pointer;

                i {
                  font-size: 18px;
                }

                &:not(:first-child) {
                  margin-left: 10px;
                }
              }

              .drag-btn {
                cursor: grab;
              }

              .visibility-btn {
                color: inherit;
                font-size: 12px;

                .btn-inner-container {
                  .btn-label {
                    vertical-align: middle;
                  }

                  .btn-icon {
                    font-size: 14px;
                    vertical-align: middle;
                  }
                }
              }
            }
          }
          .item-options-container {
            color: #676767;

            .option-list {
              padding-left: 15px;

              .option-item {
                padding: 3px 0;

                &::before {
                  content: '-';
                  margin-right: 10px;
                }

                &.knockout {
                  color: #F56C6C;
                }

                .knockout-tag {
                  height: auto;
                  margin: 0 10px;
                  padding: 1px 5px;
                  border-radius: 15px;
                  line-height: normal;
                }
              }
            }
          }
        }
      }

      .add-btn-group {
        .add-btn {
          display: inline-block;
          width: 100%;
          margin: 16px auto 0;
          padding: 14px 0;
          border: 1px dashed #0096cc;
          border-radius: 0;
          font-size: 14px;

          ::v-deep .iconfont {
            font-size: 14px;
          }

          &:first-child {
            margin-top: 24px;
          }
        }
      }

      .language-table {
        border: 1px solid #e4e7ed;
        margin-bottom: 8px;

        .head {
          display: flex;
          align-items: center;
          height: 40px;
          line-height: 40px;
          padding: 0 25px;
          border-bottom: 1px solid #dcdfe6;

          .title {
            flex: 1;
            display: inline-block;
            color: #606266;
            font-size: 15px;
            cursor: pointer;
          }

          .fold-btn {
            display: inline-block;
            transition: transform 0.3s;

            &:hover {
              cursor: pointer;
            }
          }

          .rotated-icon {
            transform: rotate(180deg);
          }

          .delete-btn {
            position: relative;
            display: inline-block;
            right: -10px;

            &:hover {
              cursor: pointer;
            }
          }
        }

        .content {
          ::v-deep {
            .language-name {
              .cell {
                padding-left: 25px;
              }
            }
          }

          .language-editor {
            margin: 25px 70px 0;
          }
        }

        ::v-deep {
          .el-table__row {
            background-color: #f7f7f7;
          }
          .el-table__cell {
            .cell {
              word-break: break-word;
            }
          }
        }
      }

      .show-table {
        border-bottom: 0;
      }

      ::v-deep {
        .el-dialog {
          width: 63vw;
          background-color: #f7f7f7;
        }
        .el-dialog__footer{
          padding: 10px 55px 20px;
        }
        .el-table__row {
          &.red {
            color: #ff0000;
          }
          background-color: #f7f7f7;
        }
      }

      .error-tips {
        padding: 20px 0;
        color: #ff0000;
        text-align: right;
      }

      .editor-dialog {
        .language-editor {
          width: 60%;
          margin: auto;
        }

        .editor-footer {
          display: flex;
          justify-content: space-between;
        }
      }
    }
  }

  .loading {
    position: absolute;
  }
}

.has-resume-view {
  .language-container {
    .language-list-editor {
      width: 90%;
      margin-left: 34px;
      .add-btn-group {}

      .language-table {
        .el-table {
          font-size: 12px;

          .el-button {
            font-size: 12px;
          }
        }
      }
    }
  }
}
</style>
